import { Images, Upload } from 'lucide-react';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoCategory, ProjectPhoto } from '../../types';
import { PhotoCard } from './PhotoCard';
import { PhotoListView } from './PhotoListView';

interface PhotoGalleryProps {
  photos: ProjectPhoto[];
  viewMode: 'gallery' | 'list' | 'timeline';
  selectedPhotos: string[];
  permissions: any;
  filterLoading: boolean;
  searchTerm: string;
  selectedCategory: PhotoCategory | '';
  selectedProject: string;
  onPhotoClick: (photo: ProjectPhoto) => void;
  onSelectionToggle: (photoId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onDownload: (photo: ProjectPhoto) => void;
  onDelete: (photo: ProjectPhoto) => void;
  getCategoryInfo: (category: PhotoCategory) => { value: string; label: string; color: string };
  formatDate: (dateString: string) => string;
  onUploadClick?: () => void;
}

export const PhotoGallery: React.FC<PhotoGalleryProps> = memo(
  ({
    photos,
    viewMode,
    selectedPhotos,
    permissions,
    filterLoading,
    searchTerm,
    selectedCategory,
    selectedProject,
    onPhotoClick,
    onSelectionToggle,
    onSelectAll,
    onClearSelection,
    onDownload,
    onDelete,
    getCategoryInfo,
    formatDate,
    onUploadClick,
  }) => {
    const { t } = useTranslation();

    if (filterLoading) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-600 border-t-transparent"></div>
            <span className="ml-3 text-sm text-gray-500">{t('common.filtering')}</span>
          </div>
        </div>
      );
    }

    if (photos.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-12 text-center">
          <Images className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-medium text-gray-900 mb-1">{t('photos.noPhotosFound')}</h3>
          <p className="text-sm text-gray-500 mb-4">
            {searchTerm || selectedCategory || selectedProject
              ? t('photos.adjustFilters')
              : permissions.upload
                ? t('photos.getStarted')
                : t('photos.noPhotosYet')}
          </p>
          {permissions.upload && onUploadClick && (
            <button
              onClick={onUploadClick}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Upload className="h-4 w-4" />
              {t('photos.uploadPhotos')}
            </button>
          )}
        </div>
      );
    }

    if (viewMode === 'gallery') {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {photos.map(photo => (
              <PhotoCard
                key={photo.photo_id}
                photo={photo}
                isSelected={selectedPhotos.includes(photo.photo_id)}
                categoryInfo={getCategoryInfo(photo.category)}
                permissions={permissions}
                onPhotoClick={onPhotoClick}
                onSelectionToggle={onSelectionToggle}
                onDownload={onDownload}
                onDelete={onDelete}
                formatDate={formatDate}
              />
            ))}
          </div>
        </div>
      );
    }

    if (viewMode === 'list') {
      return (
        <PhotoListView
          photos={photos}
          selectedPhotos={selectedPhotos}
          permissions={permissions}
          onPhotoClick={onPhotoClick}
          onSelectionToggle={onSelectionToggle}
          onSelectAll={onSelectAll}
          onClearSelection={onClearSelection}
          onDownload={onDownload}
          onDelete={onDelete}
          getCategoryInfo={getCategoryInfo}
          formatDate={formatDate}
        />
      );
    }

    return null;
  }
);

PhotoGallery.displayName = 'PhotoGallery';
